import type { CampaignReportDto } from '@carbonwebmail/contracts'
import type { Bar } from '../../ui/BarChart.js'
import { BarChart } from '../../ui/BarChart.js'
import { count, when } from '../../ui/format.js'

/**
 * When the mail actually went out.
 *
 * The figures above this say how much; this says when, and the two are not the
 * same question. A campaign to forty thousand people runs for hours, and the
 * difference between "eleven rejections spread across the afternoon" and
 * "eleven rejections in one minute at 3pm" is the difference between bad
 * addresses and a provider having a bad twenty minutes. Only one of those is
 * worth ringing anybody about.
 *
 * Stamped from the send log, so the first bar is when the provider answered the
 * first message rather than when somebody pressed send. On a large campaign
 * those are hours apart, and the gap is not a defect: the queue is drained at a
 * rate the account is allowed to send at.
 *
 * One bar is one slice and its height is the whole slice. The colours say only
 * whether the slice was clean, contained something ambiguous, or contained a
 * refusal — a stacked bar would put three rounded proportions in one column and
 * stop adding up to the figures above.
 */

type Bucket = CampaignReportDto['timeline'][number]

export function SendTimeline({ timeline }: { readonly timeline: CampaignReportDto['timeline'] }) {
  const first = timeline[0]
  const last = timeline[timeline.length - 1]

  // Nothing has been attempted, so there is no send to draw. An axis with no
  // bars on it would look like a send that reached nobody.
  if (first === undefined || last === undefined) return null

  return (
    <section aria-label="Timeline" className="mt-6">
      <h2 className="font-bold text-[15px] text-t1">Timeline</h2>
      <p className="mt-1 mb-3 text-[13px] text-t2">
        {span(first, last)} Times are when the provider answered, not when you pressed send — a
        large campaign is released at the rate your account is allowed to send at.
      </p>
      <BarChart bars={timeline.map(toBar)} />
    </section>
  )
}

/**
 * The sentence above the chart.
 *
 * Two stamps rather than a duration. "Took about three hours" reads as a
 * finished thing, and a campaign whose queue is still draining has not
 * finished — the second stamp is the last answer so far, which stays true
 * either way.
 */
function span(first: Bucket, last: Bucket): string {
  if (first.at === last.at) return `The first answer came ${when(first.at)}.`

  return `Answers came from ${when(first.at)} to ${when(last.at)}.`
}

function toBar(bucket: Bucket): Bar {
  const { accepted, duplicate, rejected, failed, unknown } = bucket.counts
  const refused = rejected + failed

  return {
    key: bucket.at,
    value: accepted + duplicate + refused + unknown,
    // Refusals first: they are the thing somebody scans this chart for. An
    // `unknown` slice is its own colour and never the danger one — the mail may
    // well have arrived. (I7)
    tone: refused > 0 ? 'warn' : unknown > 0 ? 'unknown' : 'ok',
    title: `${when(bucket.at)} — ${figures(bucket)}`,
    label: CLOCK.format(new Date(bucket.at)),
  }
}

/** Only what happened. A slice listing four zeroes says nothing four times. */
function figures(bucket: Bucket): string {
  const said = Object.entries(bucket.counts)
    .filter(([, value]) => value > 0)
    .map(([name, value]) => `${count(value)} ${name}`)

  return said.length === 0 ? 'nothing' : said.join(', ')
}

/** Pinned like every other format in the product, so it reads the same everywhere. */
const CLOCK = new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' })
